/* eslint-disable @next/next/no-img-element */
import { useStore } from "@features/common"
import { observer } from "mobx-react"
import parse from "react-html-parser"
import { BreakPoints } from "styles/styles"
import { CtaButton } from "@ui"
import { useEffect, useState } from "react"

import HeroBg from "./hero-bg.png"
import HeroBgMobile from "./hero-bg-mobile.png"
import HeroBottomBg from "./hero-bottom-bg.svg"
import HeroBottomBgMobile from "./hero-bottom-bg-mobile.svg"
import HeroIllustration from "./hero-illustration.png"

const Hero = () => {
  const {
    ResponsiveModel: { width },
    GlobalModel: { lang },
    VacanciesModel: { getTemplate },
  } = useStore()
  const isMobile = width > 0 && width < BreakPoints.tablet

  const heroTitle =
    getTemplate("careers.hero.title", false) ||
    'The big <span class="text-red-300 line-through">Idea</span>'
  const heroDescription =
    getTemplate("careers.hero.description") ||
    "We’re here to make dating fun and exciting. At Pure, we don’t just ship features. You’ll shape how people connect, explore, and vibe."
  const heroCta = getTemplate("careers.hero.cta") || "See open roles"
  const heroCtaLink =
    getTemplate("careers.hero.ctaLink") || "https://pure.careers.hibob.com/jobs"
  const heroPrivacy = parse(
    getTemplate("careers.hero.privacy", false) ||
      `We process applicant data as described in our <a class="underline" href="/${lang}/gdpr">Privacy Policy for job applications.</a>`,
  )

  const [ctaRotation, setCtaRotation] = useState(4)

  useEffect(() => {
    if (isMobile) {
      setCtaRotation(-4)
    } else {
      setCtaRotation(4)
    }
  }, [isMobile])

  return (
    <section
      className="mb-[140px] laptop:mb-[70px] pb-[0px] laptop:pb-[200px] relative w-full mobile-l:h-auto bg-no-repeat bg-cover bg-center"
      style={{
        backgroundImage: `url(${isMobile ? HeroBgMobile.src : HeroBg.src})`,
      }}
    >
      <div className="relative max-w-screen-desktop-l mx-auto px-5 desktop:pr-[100px] flex flex-col laptop-lg:flex-row-reverse">
        <img
          src={HeroIllustration.src}
          alt="Hero Illustration"
          className="max-w-none absolute z-1 top-[-20px] tablet:top-[20px] left-[-130px] tablet:left-[-320px] desktop-l:left-[-270px] h-[290px] w-auto tablet:h-auto tablet-s:max-h-[500px] tablet:max-h-[500px] laptop-lg:max-h-[600px]"
        />
        <div className="relative z-1 w-full max-w-[360px] tablet:max-w-[500px] pr-4 pt-[230px] tablet:pt-[540px] laptop-lg:pt-[0px] top-[70px] laptop-lg:top-[70px]">
          <div className="w-fit mb-[25px] tablet:mb-[50px] font-quirk-bold text-[44px] leading-[48px] tablet:text-[64px] tablet:leading-[70px] tracking-[4px] uppercase -rotate-[4deg]">
            {parse(heroTitle)}
          </div>
          <p className="w-fit ml-[10px] tablet:ml-[50px] mb-[25px] tablet:mb-[55px] font-sans text-[16px] tablet:text-[24px] leading-[20px] tablet:leading-[32px] -rotate-[2deg]">
            {heroDescription}
          </p>
          <div className="ml-[10px]">
            <a href={heroCtaLink} target="_blank" rel="noopener noreferrer">
              <CtaButton text={heroCta} rotation={ctaRotation} />
            </a>
          </div>
          <div className="-rotate-[2deg] laptop:rotate-[4deg] mt-[25px] laptop:mt-[15px] laptop:ml-[20px] w-[90%] laptop:w-[60%] font-sans-bold text-black/30 leading-[17px] text-[9px]">
            {heroPrivacy}
          </div>
        </div>
      </div>
      {/* The white shape starts just under the illustration, which ends around
          280px, and runs to the bottom of the section so the background cannot
          show through beneath it. The height is explicit on purpose: an svg is a
          replaced element, so height:auto would take its intrinsic height and the
          bottom anchor would be ignored. Tying it to the section rather than a
          fixed pixel height keeps it correct when translated copy makes the
          section taller.

          The offset is written once, as --shape-top, and the height is worked out
          from it. The extra 1px is the overhang that hides the seam, the same one
          the desktop shape gets from bottom-[-1px]. Moving the shape now means
          editing one number. */}
      <HeroBottomBgMobile
        viewBox="0 0 375 237"
        className="tablet-s:hidden block absolute [--shape-top:280px] top-[var(--shape-top)] h-[calc(100%_-_var(--shape-top)_+_1px)] right-0 w-full pointer-events-none select-none"
      />
      <HeroBottomBg
        viewBox="0 0 1440 312"
        className="tablet-s:block hidden absolute bottom-[-1px] right-0 w-full pointer-events-none select-none"
      />
    </section>
  )
}

const HeroComponent = observer(Hero)
export { HeroComponent }
